import React, { useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  DocumentTextIcon,
  LinkIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { Ticket } from 'lucide-react';
import { useQuery, useInfiniteQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Route, Switch, useLocation } from 'react-router-dom';
import { taxIdTypes } from '@repo/checkout/lib/tax-id';

import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import useSubscriptionQueries from '@dashboard/hooks/queries/useSubscriptionQueries';
import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';
import useSubmissionQueries from '@dashboard/hooks/queries/useSubmissionQueries';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import { customerHeldTicketsKey, customerKey } from '@dashboard/lib/queryKeys';
import { useCustomerOrigin } from '@dashboard/hooks/useCustomerOrigin';
import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { formatDateTime } from '@dashboard/lib/format-date';
import { toast } from '@dashboard/context/ToastContext';

import { fetcher } from '@dashboard/services/request';
import { countries } from '@dashboard/services/countries/countries';

import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardContent from '@dashboard/components/CardContent';
import Dash from '@dashboard/components/Dash';
import NavTabs from '@dashboard/components/NavTabs';
import Spinner from '@dashboard/components/Spinner';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { getPaymentColumns } from '@dashboard/scenes/PaymentsCharges';
import { getBookingColumns } from '@dashboard/scenes/PaymentsBookings';
import { getSubscriptionColumns } from '@dashboard/scenes/PaymentsSubscriptions';
import { getSubmissionColumns } from '@dashboard/scenes/Submissions';
import { AddressLines, getAddressLines } from '@dashboard/components/AddressSummary';
import Form from '@dashboard/components/Formik/Form';
import Dialog from '@dashboard/components/Dialog';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import { useBlockEmailAction } from '@dashboard/components/BlockEmailAction';
import BlockedBadge from '@dashboard/components/BlockedBadge';
import StatusBadge from '@dashboard/components/StatusBadge';
import { Button as UiButton } from '@dashboard/components/ui/button';
import TicketCode from '@dashboard/components/TicketCode';
import {
  Action,
  ActionGroup,
  CopyButton,
  OrderActionsColumn,
  OrderPageHeader,
  OrderPageSkeleton,
  OrderTableCard,
  stripeIdRow,
} from '@dashboard/components/OrderPage';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';

const getTabs = (customer, base) => [
  {
    name: 'Details',
    href: `${base}`,
    render: () => <CustomerDetails customer={customer} />,
  },
  {
    name: 'One-time',
    href: `${base}/payments`,
    render: () => <CustomerCharges customer={customer} />,
  },
  {
    name: 'Subscriptions',
    href: `${base}/subscriptions`,
    render: () => <CustomerSubscriptions customer={customer} />,
  },
  {
    name: 'Bookings',
    href: `${base}/bookings`,
    render: () => <CustomerBookings customer={customer} />,
  },
  {
    name: 'Submissions',
    href: `${base}/submissions`,
    render: () => <CustomerSubmissions customer={customer} />,
  },
];

export default function CustomerDetail({ match }) {
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const { getCustomerById } = useCustomerQueries();
  const location = useLocation();

  const { data: customer, error } = useQuery({
    queryKey: customerKey(match.params.id),
    queryFn: () => getCustomerById(match.params.id),
  });

  React.useEffect(() => {
    if (customer?.seller) {
      setAdminGhostingSellerId(customer.seller);
    }
  }, [customer?.seller, setAdminGhostingSellerId]);

  if (!customer && !error) {
    return <OrderPageSkeleton variant="customer" tabs={5} />;
  }

  if (error) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs(customer, match.url);

  return (
    <>
      <TrackRecent
        type="customer"
        id={customer._id}
        label={customer.name || customer.email}
        subtitle={customer.email}
        path={`/customers/${customer._id}`}
      />
      <Card variant="root" contentColumn>
        <CardHeader variant="root">
          <OrderPageHeader
            title={customer.name || customer.email || 'Name not provided'}
            badges={
              <>
                <StatusBadge status="hidden" label="Customer" />
                {customer.name ? null : <BlockedBadge email={customer.email} />}
              </>
            }
            date={
              <>
                {customer.name && customer.email && (
                  <div className="group flex flex-wrap items-center gap-x-2 text-gray-900 dark:text-foreground">
                    <span className="wrap-anywhere">{customer.email}</span>
                    <CopyButton text={customer.email} />
                    <BlockedBadge email={customer.email} />
                  </div>
                )}
                <div>Since {formatDateTime(customer.createdAt)}</div>
              </>
            }
          />

          <NavTabs aria-label="Customer detail">
            {tabs.map(tab => (
              <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
                {tab.name}
              </NavTabs.Item>
            ))}
          </NavTabs>
        </CardHeader>

        <CardContent variant="compact">
          <Switch>
            {tabs.map(tab => (
              <Route key={tab.href} path={tab.href} render={tab.render} exact />
            ))}
          </Switch>
        </CardContent>
      </Card>
    </>
  );
}

const textRow = (label, value) => ({ label, value: value || <Dash />, copy: value || null });

const emailRow = (label, email) => ({
  label,
  value: email ? (
    <Link to={`mailto:${email}`} blank className="break-all">
      {email}
    </Link>
  ) : (
    <Dash />
  ),
  copy: email || null,
});

/** An address as right-aligned lines, copied one per line; a dash when there is nothing to show. */
const addressRow = (label, lines, children) => ({
  label,
  value: lines.length ? <span className="inline-block text-right">{children}</span> : <Dash />,
  copy: lines.length ? lines.join('\n') : null,
});

/** The tax ID's type as the checkout labels it ("UK VAT"); the raw type when the list cannot tell. */
const getTaxIdLabel = ({ taxId, taxIdType, address }) => {
  if (!taxId) return null;
  const ofType = taxIdTypes.filter(type => type.type === taxIdType);
  const id = taxId.toUpperCase();
  const match =
    ofType.find(type => type.alpha2 === address?.country && type.regex.test(id)) ||
    ofType.find(type => type.regex.test(id)) ||
    ofType.find(type => id.startsWith(type.alpha2)) ||
    (ofType.length === 1 ? ofType[0] : null);
  return match?.label || (taxIdType ? taxIdType.replace(/_/g, ' ').toUpperCase() : null);
};

const ORIGINS = {
  link_in_bio: { label: 'Link in bio sign-up', Icon: LinkIcon },
  payment: { label: 'Payment', Icon: BanknotesIcon, screen: 'payments' },
  booking: { label: 'Booking', Icon: TicketIcon, screen: 'bookings' },
  subscription: { label: 'Subscription', Icon: ArrowPathIcon, screen: 'subscriptions' },
  submission: { label: 'Form submission', Icon: DocumentTextIcon, screen: 'submissions' },
};

function CustomerOrigin({ customer }) {
  const { seller } = useAuth();
  const recordHref = usePageRecordHref();
  const { data } = useCustomerOrigin(customer);
  const origin = data?.origin;
  if (!origin) return null;

  const { label, Icon, screen } = ORIGINS[origin.kind];
  const to = screen
    ? recordHref(screen, origin.recordId)
    : hasLinkInBioLab(seller)
      ? '/link-in-bio'
      : null;
  const content = (
    <>
      <Icon className="w-4 h-4 shrink-0" />
      <span className="min-w-0 truncate">
        {origin.pageTitle ? `${label} · ${origin.pageTitle}` : label}
      </span>
    </>
  );

  return (
    <ActionGroup title="Origin">
      {to ? (
        <Link to={to} className="flex w-full items-center gap-x-2">
          {content}
        </Link>
      ) : (
        <div className="flex w-full items-center gap-x-2">{content}</div>
      )}
      <div className="text-xs text-gray-500 dark:text-muted-foreground">
        {formatDateTime(origin.createdAt)}
      </div>
    </ActionGroup>
  );
}

function CustomerDetails({ customer }) {
  const { seller, isAdminLoggedInAsSeller } = useAuth();
  const [editing, setEditing] = useState(false);
  const blockEmail = useBlockEmailAction(customer);
  const shipping = customer.shipping;
  const shippingLines = [
    shipping?.name,
    shipping?.phone,
    ...getAddressLines(shipping?.address),
  ].filter(Boolean);

  const rows = [
    textRow('Name', customer.name),
    textRow('Company name', customer.companyName),
    { ...textRow('Tax ID', customer.taxId), sub: getTaxIdLabel(customer) },
    emailRow('Email', customer.email),
    emailRow('Billing email', customer.billingEmail),
    textRow('Phone', customer.phone),
    addressRow(
      'Billing address',
      getAddressLines(customer.address),
      <AddressLines address={customer.address} />,
    ),
    addressRow(
      'Shipping address',
      shippingLines,
      <>
        {shipping?.name && <div>{shipping.name}</div>}
        {shipping?.phone && <div>{shipping.phone}</div>}
        <AddressLines address={shipping?.address} />
      </>,
    ),
    stripeIdRow({
      label: 'Stripe customer ID',
      id: customer.stripeCustomerId,
      path: `/customers/${customer.stripeCustomerId}`,
      livemode: true,
      seller,
      isAdminLoggedInAsSeller,
    }),
    { label: 'Customer ID', value: `${customer._id}` },
  ];

  return (
    <>
      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          <OrderTableCard title="Customer details" rows={rows} />
        </div>

        {/* 54px = the card's padding (16) + the heading (26) + the table's top margin (12), so both boxes start level. */}
        <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
          <OrderActionsColumn>
            <CustomerOrigin customer={customer} />
            <ActionGroup title="Customer">
              <Action
                icon={PencilSquareIcon}
                label="Edit customer"
                onClick={() => setEditing(true)}
              />
              {blockEmail.blocked ? (
                <Action
                  icon={NoSymbolIcon}
                  label="Unblock email"
                  onClick={blockEmail.unblock}
                  loading={blockEmail.isPending}
                />
              ) : (
                <Action
                  icon={NoSymbolIcon}
                  label="Block email"
                  onClick={blockEmail.block}
                  off={!customer.email}
                  title="No email to block"
                />
              )}
            </ActionGroup>
            <ActionGroup title="Stripe">
              <Action
                icon={ArrowTopRightOnSquareIcon}
                label="View in Stripe"
                to={getStripeDashboardUrl(`/customers/${customer.stripeCustomerId}`, {
                  seller,
                  isAdminLoggedInAsSeller,
                })}
                blank
                off={!customer.stripeCustomerId}
                title="No Stripe customer"
              />
            </ActionGroup>
          </OrderActionsColumn>
        </div>
      </div>

      <CustomerEditDialog customer={customer} open={editing} onOpenChange={setEditing} />
      {blockEmail.dialog}
    </>
  );
}

function CustomerCharges({ customer }) {
  const recordHref = usePageRecordHref();
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['charges', customer._id],
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
          pageType: 'checkout',
          orderStatus: 'all',
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  const columns = React.useMemo(() => getPaymentColumns({ options: { hideCustomer: true } }), []);

  const charges = React.useMemo(
    () =>
      data?.pages?.flatMap(page =>
        page.data.filter(charge => !charge.tickets || charge.tickets.length === 0),
      ) ?? [],
    [data],
  );

  return (
    <CustomerDetailList
      columns={columns}
      data={charges}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Unable to load payments. Please try again or contact support."
      emptyState={{
        title: 'No payments yet',
        subtitle: 'Payments will show up here.',
      }}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      getRowHref={charge => recordHref('payments', charge._id, charge.page)}
    />
  );
}

function CustomerBookings({ customer }) {
  const recordHref = usePageRecordHref();
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['charges', 'bookings', customer._id],
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
          pageType: 'event',
          orderStatus: 'all',
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  const columns = React.useMemo(() => getBookingColumns({ options: { hideCustomer: true } }), []);

  const bookings = React.useMemo(
    () =>
      data?.pages?.flatMap(page =>
        page.data.filter(charge => charge.tickets && charge.tickets.length > 0),
      ) ?? [],
    [data],
  );

  return (
    <>
      <CustomerHeldTickets customer={customer} />
      <CustomerDetailList
        columns={columns}
        data={bookings}
        isLoading={isLoading}
        isError={isError}
        errorMessage="Unable to load event bookings. Please try again or contact support."
        emptyState={{
          title: 'No event bookings yet',
          subtitle: 'Event bookings will show up here.',
        }}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        getRowHref={charge => recordHref('bookings', charge._id, charge.page)}
      />
    </>
  );
}

const heldTicketColumns = [
  {
    id: 'event',
    header: 'Event',
    cell: ({ row }) => <div className="font-medium">{row.original.event.title}</div>,
  },
  { id: 'ticket', header: 'Ticket', cell: ({ row }) => row.original.ticketTypeName },
  {
    id: 'ticketShortId',
    header: 'Ticket code',
    cell: ({ row }) => <TicketCode code={row.original.ticketShortId} />,
  },
  {
    id: 'bookedBy',
    header: 'Booked by',
    cell: ({ row }) => {
      const { bookedBy } = row.original;
      if (!bookedBy) return <Dash />;
      return (
        <div>
          <div>{bookedBy.name || bookedBy.email}</div>
          {bookedBy.name ? (
            <div className="text-sm text-gray-500 dark:text-muted-foreground">{bookedBy.email}</div>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) =>
      row.original.status === 'CANCELED' ? (
        <StatusBadge status="canceled" label="Canceled" />
      ) : (
        <StatusBadge status="paid" label="Valid" />
      ),
  },
  {
    id: 'checkedIn',
    header: 'Checked in',
    cell: ({ row }) =>
      row.original.latestCheckIn === 'CHECKEDIN' ? (
        <StatusBadge status="paid" label="Checked in" />
      ) : (
        <StatusBadge status="hidden" label="Pending" />
      ),
  },
];

// Someone else booked and named this customer as the attendee, matched on their email.
function CustomerHeldTickets({ customer }) {
  const { sellerId, token } = useAuth();
  const recordHref = usePageRecordHref();
  const { data } = useQuery({
    queryKey: customerHeldTicketsKey(customer._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/customers/${customer._id}/held-tickets`, token),
  });
  const [open, setOpen] = useState(false);
  if (!data?.length) return null;
  const who = customer.name || customer.email;
  const count = `${data.length} ticket${data.length === 1 ? '' : 's'}`;
  return (
    <div className="px-4 pt-4">
      <div className="inline-flex items-center gap-3 text-sm">
        <Ticket className="size-4 shrink-0" aria-hidden="true" />
        <span>
          {who} also holds {count} on bookings someone else made.
        </span>
        <UiButton variant="outline" size="sm" onClick={() => setOpen(true)}>
          View
        </UiButton>
      </div>
      <Dialog
        title="Tickets on other bookings"
        size="5xl"
        trigger={null}
        open={open}
        onOpenChange={setOpen}
        onCancel={() => setOpen(false)}
        confirmButton={null}
        cancelButton={null}
      >
        <DataGrid
          columns={heldTicketColumns}
          data={data}
          getRowHref={ticket => recordHref('attendees', ticket._id, ticket.event._id)}
        />
      </Dialog>
    </div>
  );
}

function CustomerSubscriptions({ customer }) {
  const recordHref = usePageRecordHref();
  const { getInfiniteSubscriptions } = useSubscriptionQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['subscriptions', customer._id],
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubscriptions(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  const columns = React.useMemo(
    () => getSubscriptionColumns({ options: { hideCustomer: true } }),
    [],
  );

  const subscriptions = React.useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  return (
    <CustomerDetailList
      columns={columns}
      data={subscriptions}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Unable to load subscriptions. Please try again or contact support."
      emptyState={{
        title: 'No subscriptions yet',
        subtitle: 'Subscriptions will show up here.',
      }}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      getRowHref={subscription => recordHref('subscriptions', subscription._id, subscription.page)}
    />
  );
}

function CustomerSubmissions({ customer }) {
  const recordHref = usePageRecordHref();
  const { getInfiniteSubmissions } = useSubmissionQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['submissions', customer._id],
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubmissions(customer.seller, {
          customer: customer._id,
          limit: 5,
          skip: pageParam * 5,
          livemode: true,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;

        return allPages.length;
      },
    });

  const columns = React.useMemo(() => getSubmissionColumns(), []);

  const submissions = React.useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  return (
    <CustomerDetailList
      columns={columns}
      data={submissions}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Unable to load submissions. Please try again or contact support."
      emptyState={{
        title: 'No submissions yet',
        subtitle: 'Submissions will show up here.',
      }}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      getRowHref={submission => recordHref('submissions', submission._id, submission.page)}
    />
  );
}

/** One tab's list: the tab name is its heading, so the grid starts straight away. */
function CustomerDetailList({
  columns,
  data,
  isLoading,
  isError,
  errorMessage,
  emptyState,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  getRowHref,
}) {
  if (isError) {
    return <div className="px-4 py-2">{errorMessage}</div>;
  }

  return (
    <div className="px-4 pt-4">
      <DataGrid
        columns={columns}
        data={data}
        isLoading={isLoading}
        emptyState={
          <EmptyState>
            <EmptyState.Title>{emptyState.title}</EmptyState.Title>
            <EmptyState.Subtitle>{emptyState.subtitle}</EmptyState.Subtitle>
          </EmptyState>
        }
        getRowHref={getRowHref}
      />

      {(hasNextPage || isFetchingNextPage) && (
        <div className="py-4">
          {!isFetchingNextPage && hasNextPage && (
            <Button onClick={fetchNextPage} appearance="light">
              Load more
            </Button>
          )}
          {isFetchingNextPage && <Spinner />}
        </div>
      )}
    </div>
  );
}

function CustomerEditDialog({ customer, open, onOpenChange }) {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCustomer } = useMutation({
    mutationFn: async values => {
      return await fetcher(`/sellers/${sellerId}/customers/${customer._id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKey(customer._id) });

      onOpenChange(false);

      toast({
        title: 'Customer details updated',
      });
    },
  });

  return (
    <Dialog
      title="Edit customer details"
      open={open}
      onOpenChange={onOpenChange}
      onCancel={() => onOpenChange(false)}
      confirmButton={null}
      cancelButton={null}
    >
      {({ close }) => {
        const fields = getCustomerEditFields(customer);

        return (
          <Form
            fields={fields}
            initialValues={{
              name: customer.name,
              companyName: customer.companyName,
              email: customer.email,
              billingEmail: customer.billingEmail,
              phone: customer.phone,
              ...(customer.address
                ? {
                    address: {
                      line1: customer.address.line1,
                      line2: customer.address.line2,
                      city: customer.address.city,
                      state: customer.address.state,
                      postalCode: customer.address.postalCode,
                      country: customer.address.country,
                    },
                  }
                : {}),
              ...(customer.shipping
                ? {
                    shipping: {
                      name: customer.shipping.name,
                      phone: customer.shipping.phone,
                      ...(customer.shipping.address
                        ? {
                            address: {
                              line1: customer.shipping.address.line1,
                              line2: customer.shipping.address.line2,
                              city: customer.shipping.address.city,
                              state: customer.shipping.address.state,
                              postalCode: customer.shipping.address.postalCode,
                              country: customer.shipping.address.country,
                            },
                          }
                        : {}),
                    },
                  }
                : {}),
            }}
            onSubmit={async (values, actions) => {
              try {
                const data = await updateCustomer(values);

                actions.setStatus('');
                actions.resetForm({
                  values: {
                    name: data.name,
                    companyName: data.companyName,
                    email: data.email,
                    billingEmail: data.billingEmail,
                    phone: data.phone,
                    ...(data.address
                      ? {
                          address: {
                            line1: data.address.line1,
                            line2: data.address.line2,
                            city: data.address.city,
                            state: data.address.state,
                            postalCode: data.address.postalCode,
                            country: data.address.country,
                          },
                        }
                      : {}),
                    ...(data.shipping
                      ? {
                          shipping: {
                            name: data.shipping.name,
                            phone: data.shipping.phone,
                            ...(data.shipping.address
                              ? {
                                  address: {
                                    line1: data.shipping.address.line1,
                                    line2: data.shipping.address.line2,
                                    city: data.shipping.address.city,
                                    state: data.shipping.address.state,
                                    postalCode: data.shipping.address.postalCode,
                                    country: data.shipping.address.country,
                                  },
                                }
                              : {}),
                          },
                        }
                      : {}),
                  },
                });

                return {
                  data: true,
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            cancelButton={{
              label: 'Cancel',
              appearance: 'light',
              onClick: close,
            }}
            formSubmitAlign="end"
          />
        );
      }}
    </Dialog>
  );
}

function getCustomerEditFields(customer) {
  return [
    {
      type: 'text',
      name: 'name',
      label: 'Name',
      _value: customer.name || '',
    },
    {
      type: 'text',
      name: 'companyName',
      label: 'Company name',
      _value: customer.companyName || '',
    },
    {
      type: 'email',
      name: 'email',
      label: 'Account email',
      _value: customer.email || '',
    },
    {
      type: 'email',
      name: 'billingEmail',
      label: 'Billing email',
      _value: customer.billingEmail || '',
    },
    {
      type: 'tel',
      name: 'phone',
      label: 'Phone',
      _value: customer.phone || '',
    },
    {
      type: 'fieldgroup',
      name: 'address',
      label: 'Billing address',
      fields: [
        {
          type: 'text',
          name: 'address.line1',
          placeholder: 'Address line 1',
          _value: customer.address?.line1 || '',
        },
        {
          type: 'text',
          name: 'address.line2',
          placeholder: 'Address line 2',
          _value: customer.address?.line2 || '',
        },
        {
          type: 'text',
          name: 'address.city',
          placeholder: 'City',
          _value: customer.address?.city || '',
        },
        {
          type: 'text',
          name: 'address.state',
          placeholder: 'State',
          _value: customer.address?.state || '',
        },
        {
          type: 'text',
          name: 'address.postalCode',
          placeholder: 'Postal code',
          _value: customer.address?.postalCode || '',
        },
        {
          type: 'select',
          name: 'address.country',
          placeholder: 'Country',
          _value: customer.address?.country || '',
          options: [
            {
              label: 'Select country',
              value: '',
            },
            ...countries.map(country => ({
              label: country.name,
              value: country.alpha2,
            })),
          ],
        },
      ],
    },
    {
      type: 'fieldgroup',
      name: 'shipping',
      label: 'Shipping address',
      fields: [
        {
          type: 'text',
          name: 'shipping.name',
          placeholder: 'Name',
          _value: customer.shipping?.name || '',
        },
        {
          type: 'tel',
          name: 'shipping.phone',
          placeholder: 'Phone',
          _value: customer.shipping?.phone || '',
        },
        {
          type: 'text',
          name: 'shipping.address.line1',
          placeholder: 'Address line 1',
          _value: customer.shipping?.address?.line1 || '',
        },
        {
          type: 'text',
          name: 'shipping.address.line2',
          placeholder: 'Address line 2',
          _value: customer.shipping?.address?.line2 || '',
        },
        {
          type: 'text',
          name: 'shipping.address.city',
          placeholder: 'City',
          _value: customer.shipping?.address?.city || '',
        },
        {
          type: 'text',
          name: 'shipping.address.state',
          placeholder: 'State',
          _value: customer.shipping?.address?.state || '',
        },
        {
          type: 'text',
          name: 'shipping.address.postalCode',
          placeholder: 'Postal code',
          _value: customer.shipping?.address?.postalCode || '',
        },
        {
          type: 'select',
          name: 'shipping.address.country',
          placeholder: 'Country',
          _value: customer.shipping?.address?.country || '',
          options: [
            {
              label: 'Select country',
              value: '',
            },
            ...countries.map(country => ({
              label: country.name,
              value: country.alpha2,
            })),
          ],
        },
      ],
    },
  ];
}
